
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import useTranslation from 'next-translate/useTranslation';
import Head from 'next/head';
import styles from './index.module.scss';
import Layout from 'components/Layout';
import { Namespace, PlaylistType, StorageKeys } from 'enums';
import { StaticPathParams } from 'typings/staticPaths';
import { BrowseSkeleton, FAQList } from 'components';
import AppLink from 'components/AppLink/AppLink';
import {
  countryFromHost,
  debounce,
  getNoTrialTranslationKeyIfNeeded,
  Permalink,
  shuffleArray,
  slugifyString,
} from 'helpers';
import { basicSEO } from 'helpers/jsx';
import { useWelcomeTheatresRow } from 'hooks';
import { authorizeUserServerSide } from 'api/firebaseAdmin';
import { GetPromotedContentResponsePlay, GetPromotedContentResponseTheatre } from 'typings/notGenerated';
import { PlayBase } from 'typings/api';
import prices from 'config/prices';
import DramoxImage from 'components/DramoxImage';
import { useAuth } from 'context';
import { useRouter } from 'next/router';
import { HomeSkeleton } from 'components/HomeSkeleton';
import { homeCls, homeContentCls } from 'constants/ui';
import { FeaturedSectionType } from 'enums/FeaturedSectionType';
import { HomepageSection } from 'typings/api/homepage';
import CategorySection from 'components/CategorySections/CategorySection';
import Trans from 'next-translate/Trans';
import storageHelper from 'helpers/storageHelper';

interface Props {
  libraryPlays: PlayBase[];
  theatres: GetPromotedContentResponseTheatre[];
  plays: GetPromotedContentResponsePlay[];
  theatresTotalCount: number;
  priceMonthly: string;
  priceYearly: string;
  priceYearlyDiscount: string;
  priceYearlyNoDiscount: string;
  locale: string;
  userSessionExists: boolean;
  countryCode: string;
  sections: HomepageSection[];
}

const Welcome = ({
  theatres,
  theatresTotalCount,
  plays,
  libraryPlays,
  priceMonthly,
  priceYearly,
  priceYearlyDiscount,
  priceYearlyNoDiscount,
  locale,
  userSessionExists,
  countryCode,
  sections,
}: Props): JSX.Element => {
  const ref = React.useRef(null);
  const { t } = useTranslation(Namespace.Welcome);
  const { isUserLoaded, user } = useAuth();
  const [checked, setChecked] = useState<'monthly' | 'yearly'>('monthly');
  const [didScrollToContent, setDidScrollToContent] = useState(false);
  const router = useRouter();

  useWelcomeTheatresRow(ref, theatres, true);

  useEffect(() => {
    if (isUserLoaded && user) {
      router.replace(Permalink.collection(locale));
    }
  }, [isUserLoaded, user]);

  useEffect(() => {
    const handleScroll = () => {
      setDidScrollToContent(window.scrollY > 370);
    };

    const debounceScroll = debounce(handleScroll);

    document.documentElement.classList.add(homeCls);

    if (window) {
      window.addEventListener('scroll', debounceScroll, false);

      return () => {
        window.removeEventListener('scroll', debounceScroll, false);
        document.documentElement.classList.remove(homeCls, homeContentCls);
      };
    }
  }, []);

  const userRelatedCountryCode = useMemo(() => {
    return countryCode;
  }, [countryCode]);

  useEffect(() => {
    document.documentElement.classList[didScrollToContent ? 'add' : 'remove'](homeContentCls);
  }, [didScrollToContent]);

  if (userSessionExists && !isUserLoaded) {
    return <HomeSkeleton countryCode={countryCode} />;
  } else if (isUserLoaded && !user) {
    // intentional empty if - do nothing
  } else if (user) {
    return <BrowseSkeleton countryCode={countryCode} />;
  }

  return (
    <div className={classNames(styles.wrapper, styles.visible)}>
      <Layout countryCode={countryCode}>
        <Head>{basicSEO(t('seo:welcome_title'), t('seo:welcome_description'))}</Head>
        <main className={classNames(styles.welcome)}>
          <div className={classNames(styles.action)}>
            <div className={classNames(styles.heading)}>
              <h1>{t(`welcome_title_${countryCode}`)}</h1>
              <p>{t(`welcome_subtitle_${countryCode}`)}</p>
              <div className={styles.buttons}>
                <AppLink href={Permalink.registerStep1(locale)}>
                  <a
                    className={classNames('btn', 'btn-primary')}
                    data-register="true"
                    data-register-from="welcome/first"
                  >
                    {t(getNoTrialTranslationKeyIfNeeded(`welcome_button_long_${countryCode}`, userRelatedCountryCode))}
                  </a>
                </AppLink>
                <AppLink href={Permalink.collection(locale)}>
                  <a className={classNames('btn', 'btn-outline-info')}>{t(`welcome_button_browse_${countryCode}`)}</a>
                </AppLink>
              </div>
              <p className={classNames(styles.subscriptionInfo)}>
                {t(`welcome_price_${countryCode}`, { monthly: priceMonthly })}
              </p>
            </div>
            <div className={styles.thumb}>
              <div className={styles.videoContainer}>
                <video playsInline loop autoPlay muted>
                  <source src={`https://42.dramoxtv.cz/video_public/hp-main-${countryCode}.mp4`} type="video/mp4" />
                </video>
              </div>
              <div className={styles.overlay} />
            </div>
          </div>

          <h2>{t('theatres_list_title')}</h2>

          <div className={classNames(styles.theaters, 'theaters', 'swipe-theater')} ref={ref}>
            <ul className={classNames('swiper-wrapper')}>
              {theatres.map((theatre) => {
                const slug = slugifyString(theatre.title);
                const { title } = theatre;
                return (
                  <li key={theatre.id} className={classNames('swiper-slide')}>
                    <DramoxImage src={theatre.poster_url} height={70} width={70} quality={90} />
                    <span className={classNames(styles.tooltiptext)}>{title}</span>
                    <AppLink href={Permalink.theatre(theatre, locale)}>
                      <a>{title}</a>
                    </AppLink>
                  </li>
                );
              })}
              {theatresTotalCount > 9 ? (
                <li className={classNames(styles.more, 'swiper-slide')}>
                  <AppLink href={Permalink.theatres(locale)}>
                    <a>+{theatresTotalCount - 9}</a>
                  </AppLink>
                </li>
              ) : null}
            </ul>
            <p>{t(`new_welcome_sec_1_title_${countryCode}`)}</p>
          </div>

          <div className={styles.sections}>
            {sections.map((s) => {
              let title = s.name;

              switch (s.type) {
                case FeaturedSectionType.POPULAR:
                  title = t('common:most_popular');
                  break;
                case FeaturedSectionType.WATCHING:
                  title = t('common:currently_watching');
                  break;
                case FeaturedSectionType.NEWEST:
                  title = t('common:recently_added');
                  break;
                case FeaturedSectionType.ORIGINALS:
                  break;
                case FeaturedSectionType.WATCHLIST:
                  title = t('common:watch_list');
                  break;
              }

              return (
                <CategorySection
                  playlistType={PlaylistType.Section}
                  title={title}
                  plays={s.plays}
                  key={s.id}
                  showResume={s.type === FeaturedSectionType.WATCHING}
                  mediumThumbs={s.type === FeaturedSectionType.CUSTOM_MEDIUM}
                  largeThumbs={s.type === FeaturedSectionType.ORIGINALS}
                  centered
                />
              );
            })}
          </div>

          <div className={styles.subscription}>
            <div className={styles.info}>
              <h2>{t(`welcome_subscription_title_${countryCode}`)}</h2>
              <ul>
                <li>{t(`welcome_subscription_1_${countryCode}`)}</li>
                <li>{t(`welcome_subscription_2_${countryCode}`)}</li>
                <li>{t(`welcome_subscription_3_${countryCode}`)}</li>
              </ul>
            </div>
            <div className={styles.buy}>
              <div>
                <label>
                  <input
                    type="radio"
                    name="subscription"
                    value="monthly"
                    onChange={() => setChecked('monthly')}
                    checked={checked === 'monthly'}
                  />
                  <span>
                    <span>
                      <Trans
                        i18nKey={`welcome:welcome_subscription_button_1_${countryCode}`}
                        values={{ priceMonthly }}
                        components={[<strong />, <br />]}
                      />
                    </span>
                  </span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="subscription"
                    value="yearly"
                    onChange={() => setChecked('yearly')}
                    checked={checked === 'yearly'}
                  />
                  <span>
                    <span>
                      <Trans
                        i18nKey={`welcome:welcome_subscription_button_2_${countryCode}`}
                        components={[<strong />, <br />, <s />]}
                        values={{ priceYearly, beforeYearly: priceYearlyNoDiscount }}
                      />
                    </span>
                  </span>
                </label>
              </div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  storageHelper.add(StorageKeys.HOMEPAGE_PLAN_TYPE, checked);
                  router.push(Permalink.registerStep1(locale));
                }}
              >
                {t(`welcome:welcome_subscription_button_activate_${countryCode}`)}
              </button>
              <p className={styles.note}>{t(`welcome:welcome_subscription_cancel_note_${countryCode}`)}</p>
            </div>
          </div>

          <div className={classNames(styles.section)}>
            <div className={classNames(styles.pic)}>
              <div className={styles.illustration}>
                <h2>{t(`new_welcome_sec_3_title_${countryCode}`)}</h2>
                <p>{t(`new_welcome_sec_3_text_${countryCode}`)}</p>
              </div>
              <div role="img" aria-label={t(`welcome_subtitle_${countryCode}`)}>
                <div className={styles.videoContainerBottom}>
                  <video playsInline loop autoPlay muted>
                    <source src={`https://42.dramoxtv.cz/video_public/hp-bottom-${countryCode}.mp4`} type="video/mp4" />
                  </video>
                </div>
                <img
                  className={styles.mockup}
                  srcSet={`/webp/imgtv_${countryCode}_1x.webp 1x, /webp/imgtv_${countryCode}_2x.webp 2x`}
                  src={`/webp/imgtv_${countryCode}_1x.webp`}
                  width={1170}
                  height={820}
                />
                <img
                  className={styles.overlay}
                  srcSet={`/webp/tv_overlay_1x.webp 1x, /webp/tv_overlay_2x.webp 2x`}
                  src="/webp/tv_overlay_1x.webp"
                  width={1170}
                  height={418}
                />
              </div>
            </div>
            <p className={styles.streamingTitle}>{t(`welcome_subtitle_${countryCode}`)}</p>
            <ul className={styles.streaming}>
              <li className={styles.samsung}>
                <AppLink href={Permalink.howToWatchSmartTVSamsung(locale)}>{t('smart-tv:samsung_title')}</AppLink>
              </li>
              <li className={styles.lg}>
                <AppLink href={Permalink.howToWatchSmartTVLG(locale)}>{t('smart-tv:lg_title')}</AppLink>
              </li>
              <li className={styles.apple}>
                <AppLink href={Permalink.howToWatchMobileIOS(locale)}>{t('mobile-apps:ios_title')}</AppLink>
              </li>
              <li className={styles.androidTv}>
                <AppLink href={Permalink.howToWatchAndroidTv(locale)}>{t('mobile-apps:android_tv_title')}</AppLink>
              </li>
              <li className={styles.android}>
                <AppLink href={Permalink.howToWatchMobileIOS(locale)}>{t('mobile-apps:android_title')}</AppLink>
              </li>
            </ul>
            <AppLink href={Permalink.registerStep1(locale)}>
              <a className={classNames('btn', 'btn-primary')} data-register="true" data-register-from="welcome/first">
                {t(getNoTrialTranslationKeyIfNeeded(`welcome_button_long_${countryCode}`, userRelatedCountryCode))}
              </a>
            </AppLink>
          </div>
          <div className={classNames(styles.fromTopGradient)}>
            <div className={classNames(styles.faq)}>
              <h2>{t('menu_faq')}</h2>
              <div id="faq">
                <FAQList
                  type="short"
                  priceMonthly={priceMonthly}
                  priceYearly={priceYearly}
                  priceYearlyDiscount={priceYearlyDiscount}
                  countryCode={countryCode}
                />
              </div>
            </div>
          </div>
        </main>
      </Layout>
    </div>
  );
};

 async function _getServerSideProps(
  ctx: GetServerSidePropsContext<StaticPathParams>,
): Promise<GetServerSidePropsResult<Props>> {
  const { locale, apiClient, userSessionExists, countryCode } = await authorizeUserServerSide(ctx);

  try {
    const { theatres, plays, total_theatres_count } = await apiClient.getHomepageContent();
    const libraryPlays = shuffleArray(await apiClient.getPlays()).slice(0, 3);
    const plans = await apiClient.getPlans();

    let priceMonthly: string;
    let priceYearly: string;
    let priceYearlyDiscount: string;
    let priceYearlyNoDiscount: string;
    const planMonthly = plans.find((p) => p.recurrence.unit === 'MONTH' && p.recurrence.length === 1);
    const planYearly = plans.find((p) => p.recurrence.unit === 'YEAR' && p.recurrence.length === 1);

    if (planMonthly && planYearly) {
      priceMonthly = planMonthly.price.formatted;
      priceYearly = planYearly.price.formatted;
      const currency = planYearly.price.currency;
      priceYearlyDiscount = priceMonthly
        .replace(',', '.')
        .replace('0', '')
        .replace(planMonthly.price.value.toString(), prices[currency].yearPriceDiff.toString());
      priceYearlyNoDiscount = priceYearly.replace(
        planYearly.price.value.toLocaleString(locale),
        Math.ceil((planMonthly.price.value ?? 0) * 12).toString(),
      );
    }

    console.log(priceMonthly, priceYearly, priceYearlyDiscount, priceYearlyNoDiscount);

    const { sections } = await apiClient.getHomepageSections(undefined, 999, /* landingPage */ true);

    return {
      props: {
        theatres,
        plays,
        theatresTotalCount: total_theatres_count,
        libraryPlays,
        priceMonthly,
        priceYearly,
        priceYearlyDiscount,
        priceYearlyNoDiscount,
        locale,
        userSessionExists,
        countryCode,
        sections,
      },
    };
  } catch (e) {
    console.log(e);
  }
}

export default Welcome;


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  